import type { ReviewableOrderResponse } from '@contracts/order/ReviewableOrderResponse';
import { toBillingCurrency } from '@contracts/payment/Currency';
import { Timestamp } from '@src/common/time/Timestamp';
import { Billing } from '@src/orders/domain/Billing';
import { OrderStatus } from '@src/orders/domain/Order';
import type { PermitStatus } from '@src/orders/domain/PermitApplication';
import { ReviewableOrder } from '@src/orders/domain/ReviewableOrder';
import { DuplicationTrigger } from '@src/orders/hooks/useDuplicatePermitAsNew';

import { OrderActionsSchema } from './OrderActionsSchema';

export const assembleReviewableOrder = (response: ReviewableOrderResponse) =>
  new ReviewableOrder({
    ...response,
    submittedDate: Timestamp.from(response.submittedDate),
    billingCurrency: response.billingCurrency,
    exchangeRate: response.exchangeRate,
    adminFeeRate: response.adminFeeRate,
    canEditAdminFeeRate: response.canEditAdminFeeRate,
    taxRate: response.taxRate,
    billing: response.fees.billing
      ? new Billing({
          billingTable: response.fees.billing,
          stateFees: response.fees.stateFees,
          serviceFees: response.fees.serviceFees,
          adminFees: response.fees.adminFees,
          taxes: response.fees.taxes,
          totalFees: response.fees.totalFees,
          totalRemaining: response.fees.totalRemaining,
        })
      : undefined,
    permitFeesTotals: {
      CAD: {
        serviceFee: response.fees.serviceFees.CAD,
        jurisdictionFee: response.fees.stateFees.CAD,
      },
      USD: {
        serviceFee: response.fees.serviceFees.USD,
        jurisdictionFee: response.fees.stateFees.USD,
      },
    },
    billingDate: response.billingDate,
    status: OrderStatus[response.status as keyof typeof OrderStatus],
    allowedActions: new Set(
      response.allowedActions.map((action) => OrderActionsSchema.cast(action)),
    ),
    permitApplicationGroups: response.permitApplicationGroups.map(
      ({ originalPermit, subsequentPermits }) => ({
        originalPermit: {
          ...originalPermit,
          status: originalPermit.status as PermitStatus,
          fees: {
            invoiceNumber: originalPermit.fees.invoiceNumber ?? undefined,
            jurisdictionFee: {
              ...originalPermit.fees.jurisdictionFee,
              currency: originalPermit.fees.jurisdictionFee.currency,
            },
            serviceFee: {
              ...originalPermit.fees.serviceFee,
              currency: originalPermit.fees.serviceFee.currency,
            },
          },
          currency: toBillingCurrency(originalPermit.currency),
        },
        subsequentPermits: subsequentPermits.map((subsequentPermit) => ({
          ...subsequentPermit,
          status: subsequentPermit.status as PermitStatus,
          fees: {
            invoiceNumber: originalPermit.fees.invoiceNumber ?? undefined,
            jurisdictionFee: {
              ...subsequentPermit.fees.jurisdictionFee,
              currency: subsequentPermit.fees.jurisdictionFee.currency,
            },
            serviceFee: {
              ...subsequentPermit.fees.serviceFee,
              currency: subsequentPermit.fees.serviceFee.currency,
            },
          },
          currency: toBillingCurrency(subsequentPermit.currency),
          trigger: DuplicationTrigger[subsequentPermit.trigger],
        })),
      }),
    ),
  });
